/**
 * Dynamic Model Fetching for A/B Testing - Sprint 11g Rosetta Stone Pattern
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️  CRITICAL: NEVER HARDCODE MODEL NAMES, LISTS, OR PATTERNS  ⚠️            ║
 * ║                                                                              ║
 * ║  All model data MUST come from remote APIs at runtime.                       ║
 * ║  Hardcoded lists become outdated within weeks as providers add new models.   ║
 * ║                                                                              ║
 * ║  ROSETTA STONE PATTERN (Sprint 11g):                                        ║
 * ║  1. Provider APIs = source of truth for model IDs (real, valid IDs)         ║
 * ║  2. OpenRouter = source for metadata (pricing, context windows)             ║
 * ║  3. ConfigService merges these for the complete model list                  ║
 * ║                                                                              ║
 * ║  ❌ FORBIDDEN:                                                                ║
 * ║     - Whitelists of model names (e.g., ['gpt-4', 'gpt-4o', ...])            ║
 * ║     - Pattern matching for specific models (e.g., id.includes('llama'))     ║
 * ║     - Hardcoded display name mappings                                        ║
 * ║     - Hardcoded deprecated model lists (provider APIs only return active)   ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 * @see docs/sprints/ownyou-sprint11g-spec.md
 */

import type { ModelConfig, LLMProvider } from '@ownyou/ab-testing';
import {
  configService,
  type EnrichedModel,
  type ProviderApiKeys,
  type ModelMetadata,
  type FallbackModel,
} from '@ownyou/llm-client';
import { getLLMProxyBaseUrl, shouldUseLLMProxy, shouldUseOpenAIProxy } from '../lib/openai-proxy';

// 5-minute cache for API-fetched models
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CachedModels {
  models: ModelConfig[];
  timestamp: number;
}

let modelsCache: CachedModels | null = null;

const CLOUD_PROXY_PROVIDERS = ['openai', 'anthropic', 'groq', 'deepinfra', 'xai'] as const;

type CloudProxyProvider = typeof CLOUD_PROXY_PROVIDERS[number];

interface ProxyModelRecord {
  id?: unknown;
  display_name?: unknown;
}

interface ProxyModelsResponse {
  data?: ProxyModelRecord[];
}

interface MetadataIndex {
  byId: Map<string, ModelMetadata>;
  byProviderId: Map<string, ModelMetadata>;
  byProviderLooseId: Map<string, ModelMetadata>;
}

/**
 * Map ConfigService provider to LLMProvider
 */
function mapToLLMProvider(provider: string): LLMProvider {
  const mapping: Record<string, LLMProvider> = {
    openai: 'openai',
    anthropic: 'claude',
    google: 'gemini',
    groq: 'groq',
    deepinfra: 'deepinfra',
    xai: 'xai',
    ollama: 'ollama',
  };
  return mapping[provider] ?? (provider as LLMProvider);
}

/**
 * Get API keys from environment variables
 */
function getApiKeys(): ProviderApiKeys {
  return {
    anthropic: undefined,
    openai: undefined,
    google: undefined,
    groq: undefined,
    deepinfra: undefined,
  };
}

async function getBrowserPrivyAccessToken(): Promise<string | null> {
  try {
    const { getAccessToken } = await import('@privy-io/react-auth');
    return getAccessToken();
  } catch {
    return null;
  }
}

/**
 * Check which API keys are configured
 */
export function getConfiguredProviders(): Record<string, boolean> {
  return {
    openai: shouldUseOpenAIProxy(),
    anthropic: shouldUseLLMProxy(),
    google: false,
    groq: shouldUseLLMProxy(),
    deepseek: false,
    deepinfra: shouldUseLLMProxy(),
    xai: shouldUseLLMProxy(),
    ollama: !!import.meta.env.VITE_OLLAMA_BASE_URL,
  };
}

function proxyProviderIsConfigured(provider: CloudProxyProvider): boolean {
  const configured = getConfiguredProviders();
  if (provider === 'anthropic') return configured.anthropic;
  return Boolean(configured[provider]);
}

function normalizeLookupKey(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/^models\//i, '').toLowerCase();
}

function normalizeLooseLookupKey(value: string): string {
  return normalizeLookupKey(value).replace(/[^a-z0-9]+/g, '');
}

function stripProviderPrefix(value: string, provider: string): string {
  return value.startsWith(`${provider}/`) ? value.slice(provider.length + 1) : value;
}

function addVersionCandidates(candidates: Set<string>, value: string): void {
  if (!value) return;

  candidates.add(value);
  candidates.add(value.replace(/-\d{8}$/, ''));

  for (const candidate of Array.from(candidates)) {
    candidates.add(candidate.replace(/(\d)-(\d)/g, '$1.$2'));
    candidates.add(candidate.replace(/(\d)\.(\d)/g, '$1-$2'));
  }
}

function getModelLookupCandidates(value: unknown, provider: string): string[] {
  const normalized = normalizeLookupKey(value);
  if (!normalized) return [];

  const roots = new Set<string>([
    normalized,
    stripProviderPrefix(normalized, provider),
  ]);

  const slashParts = normalized.split('/');
  const finalSegment = slashParts[slashParts.length - 1];
  if (finalSegment) {
    roots.add(finalSegment);
  }

  const candidates = new Set<string>();
  for (const root of roots) {
    addVersionCandidates(candidates, root);
  }

  return Array.from(candidates).filter(Boolean);
}

function metadataProviderMatches(metadata: ModelMetadata, provider: CloudProxyProvider): boolean {
  return normalizeLookupKey(metadata.provider) === provider;
}

function addMetadataIdentifier(
  index: MetadataIndex,
  provider: string,
  metadata: ModelMetadata,
  value: unknown,
  includeGlobal: boolean,
): void {
  for (const candidate of getModelLookupCandidates(value, provider)) {
    const providerCandidate = `${provider}:${candidate}`;
    if (includeGlobal) index.byId.set(candidate, metadata);
    index.byProviderId.set(providerCandidate, metadata);

    const looseCandidate = normalizeLooseLookupKey(candidate);
    if (looseCandidate) {
      index.byProviderLooseId.set(`${provider}:${looseCandidate}`, metadata);
    }
  }
}

function buildMetadataIndex(metadataById: Record<string, ModelMetadata>): MetadataIndex {
  const index: MetadataIndex = {
    byId: new Map(),
    byProviderId: new Map(),
    byProviderLooseId: new Map(),
  };

  for (const [id, metadata] of Object.entries(metadataById)) {
    const provider = normalizeLookupKey(metadata.provider);
    if (!provider) continue;

    addMetadataIdentifier(index, provider, metadata, id, true);
    addMetadataIdentifier(index, provider, metadata, metadata.id, true);
    addMetadataIdentifier(index, provider, metadata, metadata.displayName, false);
  }

  return index;
}

function findProxyModelMetadata(
  provider: CloudProxyProvider,
  record: ProxyModelRecord,
  metadataIndex: MetadataIndex,
): ModelMetadata | undefined {
  const providerKey = normalizeLookupKey(provider);

  for (const candidate of getModelLookupCandidates(record.id, providerKey)) {
    const providerMatch = metadataIndex.byProviderId.get(`${providerKey}:${candidate}`);
    if (providerMatch) return providerMatch;

    const exactMatch = metadataIndex.byId.get(candidate);
    if (exactMatch && metadataProviderMatches(exactMatch, provider)) return exactMatch;

    const looseCandidate = normalizeLooseLookupKey(candidate);
    const looseMatch = metadataIndex.byProviderLooseId.get(`${providerKey}:${looseCandidate}`);
    if (looseMatch) return looseMatch;
  }

  for (const candidate of getModelLookupCandidates(record.display_name, providerKey)) {
    const providerMatch = metadataIndex.byProviderId.get(`${providerKey}:${candidate}`);
    if (providerMatch) return providerMatch;

    const looseCandidate = normalizeLooseLookupKey(candidate);
    const looseMatch = metadataIndex.byProviderLooseId.get(`${providerKey}:${looseCandidate}`);
    if (looseMatch) return looseMatch;
  }

  return undefined;
}

function proxyModelToModelConfig(
  provider: CloudProxyProvider,
  record: ProxyModelRecord,
  metadata: ModelMetadata | undefined,
): ModelConfig | null {
  if (typeof record.id !== 'string' || !record.id.trim()) return null;

  const displayName = metadata?.displayName
    ?? (typeof record.display_name === 'string' && record.display_name.trim() ? record.display_name : record.id);

  return {
    provider: mapToLLMProvider(provider),
    model: record.id,
    displayName,
    pricing: metadata?.pricing ?? null,
  };
}

async function fetchProxyProviderModels(
  provider: CloudProxyProvider,
  authToken: string,
  metadataIndex: MetadataIndex,
): Promise<ModelConfig[]> {
  try {
    const response = await fetch(`${getLLMProxyBaseUrl(provider)}/models`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        Accept: 'application/json',
      },
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      console.warn(`[FetchModels] ${provider} proxy model fetch failed: ${response.status}`);
      return [];
    }

    const data = await response.json() as ProxyModelsResponse;
    if (!Array.isArray(data.data)) return [];

    return data.data
      .map((record) => proxyModelToModelConfig(provider, record, findProxyModelMetadata(provider, record, metadataIndex)))
      .filter((model): model is ModelConfig => Boolean(model));
  } catch (error) {
    console.warn(`[FetchModels] ${provider} proxy model fetch failed:`, error);
    return [];
  }
}

async function addProxyModels(models: ModelConfig[]): Promise<void> {
  if (!shouldUseLLMProxy()) return;

  const authToken = await getBrowserPrivyAccessToken();
  if (!authToken) {
    console.log('[FetchModels] Server-proxy models skipped until Privy auth is ready');
    return;
  }

  try {
    const config = await configService.getConfig();
    const metadataIndex = buildMetadataIndex(config.models as Record<string, ModelMetadata>);
    const providers = CLOUD_PROXY_PROVIDERS.filter(proxyProviderIsConfigured);
    const proxyModelGroups = await Promise.all(
      providers.map((provider) => fetchProxyProviderModels(provider, authToken, metadataIndex)),
    );
    let added = 0;
    for (const model of proxyModelGroups.flat()) {
      if (models.some((existing) => existing.provider === model.provider && existing.model === model.model)) continue;
      models.push(model);
      added += 1;
    }
    console.log(`[FetchModels] Added ${added} server-proxy models from provider endpoints`);
  } catch (error) {
    console.warn('[FetchModels] Server-proxy model fallback failed:', error);
  }
}

/**
 * Convert EnrichedModel to ModelConfig (including pricing from llm-prices.com)
 */
function enrichedToModelConfig(model: EnrichedModel): ModelConfig {
  return {
    provider: mapToLLMProvider(model.provider),
    model: model.id,
    displayName: model.displayName,
    pricing: model.pricing ?? null,
  };
}

/**
 * Fetch local Ollama models (these require local server, not API key)
 */
async function fetchLocalOllamaModels(): Promise<ModelConfig[]> {
  const baseUrl = import.meta.env.VITE_OLLAMA_BASE_URL || 'http://localhost:11434';

  try {
    const response = await fetch(`${baseUrl}/api/tags`, {
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    const models: ModelConfig[] = (data.models || []).map((m: { name: string }) => ({
      provider: 'ollama' as LLMProvider,
      model: m.name,
      displayName: m.name
        .split(/[-_/]/)
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
    }));

    console.log('[FetchModels] Local Ollama models:', models.length);
    return models;
  } catch {
    console.log('[FetchModels] Ollama not running (this is OK)');
    return [];
  }
}

/**
 * Get ConfigService fallback models as ModelConfig[]
 * Used when provider APIs are unavailable
 */
export async function getConfigFallbackModels(): Promise<ModelConfig[]> {
  try {
    const fallbacks = await configService.getFallbackModels();
    return fallbacks.map((f: FallbackModel) => ({
      provider: mapToLLMProvider(f.provider),
      model: f.model,
      displayName: f.displayName,
    }));
  } catch (error) {
    console.error('[FetchModels] Failed to get ConfigService fallbacks:', error);
    return [];
  }
}

/**
 * Main function: Fetch available models using Rosetta Stone pattern
 *
 * ROSETTA STONE FLOW:
 * 1. ConfigService.refreshModels() fetches from Provider APIs (real model IDs)
 * 2. ConfigService enriches with OpenRouter metadata (pricing, context windows)
 * 3. Result: Real IDs that work with provider APIs + complete metadata
 *
 * NO MORE:
 * - Deprecated model lists (provider APIs only return active models)
 * - ID aliasing (we use real provider IDs now)
 * - 404 errors from invalid model IDs
 *
 * Results are cached for 5 minutes.
 */
export async function fetchAvailableModels(forceRefresh = false): Promise<ModelConfig[]> {
  // Check cache
  if (!forceRefresh && modelsCache) {
    const age = Date.now() - modelsCache.timestamp;
    if (age < CACHE_TTL_MS) {
      console.log('[FetchModels] Using cached models (age: ' + Math.round(age / 1000) + 's)');
      return modelsCache.models;
    }
  }

  console.log('[FetchModels] Starting Rosetta Stone model fetch...');

  const apiKeys = getApiKeys();
  const configured = getConfiguredProviders();
  let models: ModelConfig[] = [];

  // Check if any API keys are configured
  const hasAnyApiKey = Object.values(apiKeys).some((key) => key);

  if (hasAnyApiKey) {
    try {
      // ROSETTA STONE: Fetch from provider APIs + enrich with OpenRouter metadata
      const enrichedModels = await configService.refreshModels(apiKeys);
      models = enrichedModels.map(enrichedToModelConfig);
      console.log(`[FetchModels] Rosetta Stone: ${models.length} models from provider APIs`);
    } catch (error) {
      console.warn('[FetchModels] Rosetta Stone fetch failed:', error);
    }
  } else {
    console.log('[FetchModels] No API keys configured, using aggregator data only');
  }

  await addProxyModels(models);

  // SUPPLEMENT: Add local Ollama models
  if (configured.ollama) {
    const ollamaModels = await fetchLocalOllamaModels();
    for (const model of ollamaModels) {
      if (!models.some((m) => m.model === model.model)) {
        models.push(model);
      }
    }
  }

  // FALLBACK: If no models from provider APIs, use ConfigService aggregator data.
  // In production proxy mode, provider endpoints are the executable source of truth;
  // aggregator-only IDs can be metadata aliases that the proxy cannot run.
  if (models.length === 0) {
    if (shouldUseLLMProxy()) {
      console.log('[FetchModels] No executable proxy models available yet');
    } else {
      console.log('[FetchModels] No models from providers, falling back to aggregator data');
      try {
        // Get models from ConfigService (llm-prices.com → OpenRouter → bundled)
        const config = await configService.getConfig();
        for (const [id, metadata] of Object.entries(config.models) as [string, ModelMetadata][]) {
          models.push({
            provider: mapToLLMProvider(metadata.provider),
            model: id,
            displayName: metadata.displayName,
            pricing: metadata.pricing ?? null,
          });
        }
        console.log(`[FetchModels] Aggregator fallback: ${models.length} models`);
      } catch (error) {
        console.warn('[FetchModels] Aggregator fallback failed:', error);
      }
    }
  }

  // FINAL FALLBACK: Use curated fallback list
  if (models.length === 0 && !shouldUseLLMProxy()) {
    console.warn('[FetchModels] All sources failed, using curated fallback list');
    models = await getConfigFallbackModels();
  }

  // Update cache
  modelsCache = {
    models,
    timestamp: Date.now(),
  };

  console.log('[FetchModels] Total models available:', models.length);
  return models;
}

/**
 * Clear the models cache
 */
export function clearModelsCache(): void {
  modelsCache = null;
  console.log('[FetchModels] Cache cleared');
}

/**
 * Get models grouped by provider
 */
export function groupModelsByProvider(models: ModelConfig[]): Record<LLMProvider, ModelConfig[]> {
  return models.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<LLMProvider, ModelConfig[]>);
}
